import type { FinancialSectionV2, FinancialVehicleV2, TilaDisclosuresV2 } from "@credit-acceptance/step6-bff-client";
import { clsx } from "clsx";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import type { FinancialAccessCheck } from "../../../../hooks/useFinancialAccessGuard";
import { formatIsoDate } from "../../../../lib/format";
import { formatCurrency, formatPercent, formatSignedCurrency } from "../../../../lib/money";
import LineItemRow from "../../components/LineItemRow";
import Section from "../../components/Section";
import AccessoriesModal from "./AccessoriesModal";
import CorrectionLetterModal from "./CorrectionLetterModal";
import type { FinancialWithFeeContext } from "./customerFees.logic";
import CustomerFeesModal from "./CustomerFeesModal";
import DeferredDownPaymentModal from "./DeferredDownPaymentModal";
import { mapItemization } from "./Financial.mapping";
import { EMPTY_VALUE, formatOptional } from "./financialFormat";
import SalesTaxModal from "./SalesTaxModal";

type ItemizationModal = "accessories" | "deferredDownPayment" | "salesTax" | "customerFees";

type LineItemLabelKey =
  | "financial.lineItems.accessories"
  | "financial.lineItems.salesTax"
  | "financial.lineItems.cashDown"
  | "financial.lineItems.totalCustomerFees";

// CSS class for underlined subtotal rows in the itemization breakdown.
const underlineClassName = "border-border border-t border-dashed";

function FinancialField({ label, value }: { readonly label: ReactNode; readonly value: string }) {
  return (
    <div className="gap-(--spacing-1) flex flex-col">
      <span className="text-primary text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold)">
        {label}
      </span>
      <span className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)">
        {value}
      </span>
    </div>
  );
}

function SubsectionHeader({ children }: { readonly children: ReactNode }) {
  return (
    <div className="bg-(--color-background-default-muted) text-primary px-(--spacing-2) py-(--spacing-1) font-(--font-weight-semibold) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) rounded">
      {children}
    </div>
  );
}

interface FinancialProperties {
  approvalId: string;
  /** From `financial.correctionLetter.hasCorrectionLetter` on the v2 dashboard response. */
  hasCorrectionLetter: boolean;
  /** From `financial.contractDate` on the v2 dashboard response. */
  contractDate?: string;
  /** From `financial.registrationState` on the v2 dashboard response. */
  registrationState?: string;
  /** From `financial.receiptDate` (contract receipt date) on the v2 dashboard response. */
  receiptDate?: string;
  /** From `financial.tila` on the v2 dashboard response. */
  tila?: TilaDisclosuresV2;
  /** From `financial.vehicle` on the v2 dashboard response. */
  vehicle?: FinancialVehicleV2;
  /** The `financial` section of the v2 dashboard response, source of the itemization and its popups. */
  financial?: FinancialSectionV2;
  /** Disables the editable controls inside popups; popups still open. */
  readOnly: boolean;
  /** Re-evaluates Financial access (spec X2); called before an editable popup opens and before each Apply. */
  onCheckAccess?: () => Promise<FinancialAccessCheck>;
}

function Financial({
  approvalId,
  hasCorrectionLetter,
  contractDate,
  registrationState,
  receiptDate,
  tila,
  vehicle,
  financial,
  readOnly,
  onCheckAccess,
}: FinancialProperties) {
  const { t } = useTranslation();

  const [isCorrectionLetterModalOpen, setIsCorrectionLetterModalOpen] = useState(false);

  const [openModal, setOpenModal] = useState<ItemizationModal | null>(null);
  const closeModal = (open: boolean) => {
    if (!open) {
      setOpenModal(null);
    }
  };

  const amounts = mapItemization(financial);
  const accessories = financial?.accessories;
  const deferredDownPayment = financial?.deferredDownPayment;
  const salesTaxLines = financial?.itemization?.salesTax?.itemization ?? [];
  const customerFees = financial?.customerFees ?? [];

  const hasAccessories = (accessories?.details?.length ?? 0) > 0;
  const hasDeferredDownPayment = (deferredDownPayment?.entries?.length ?? 0) > 0;
  const hasSalesTaxLines = salesTaxLines.length > 0;
  // The Customer Fee popup is only reachable when the backend flags the fees as manual.
  const canOpenCustomerFees = financial?.feeManual === true && customerFees.length > 0;

  // The editable popups re-check access first; a section that has become hidden doesn't open.
  const openGuarded = async (open: () => void) => {
    const access = await onCheckAccess?.();
    if (access?.visible === false) {
      return;
    }
    open();
  };

  const openItemizationModal = (modal: ItemizationModal) =>
    modal === "customerFees" ? openGuarded(() => setOpenModal(modal)) : setOpenModal(modal);

  const renderLink = (labelKey: LineItemLabelKey, modal: ItemizationModal) => (
    <button
      type="button"
      onClick={() => void openItemizationModal(modal)}
      className="text-primary cursor-pointer text-left underline underline-offset-4"
    >
      {t(labelKey)}
    </button>
  );

  const renderLabel = (labelKey: LineItemLabelKey, modal: ItemizationModal, isLink: boolean) =>
    isLink ? renderLink(labelKey, modal) : t(labelKey);

  const signedAmount = (amount?: number, negativeZero = false) =>
    formatOptional(amount, (value) => formatSignedCurrency(value, negativeZero));

  return (
    <Section title={t("financial.sectionTitle")}>
      {/* Header fields */}
      <div className="gap-(--spacing-4) border-border mb-(--spacing-4) pb-(--spacing-4) grid grid-cols-2 border-b sm:grid-cols-4">
        <FinancialField label={t("financial.receiptDate")} value={formatIsoDate(receiptDate) || EMPTY_VALUE} />
        <FinancialField label={t("financial.contractDate")} value={formatIsoDate(contractDate) || EMPTY_VALUE} />
        <FinancialField label={t("financial.registrationState")} value={registrationState || EMPTY_VALUE} />
        <FinancialField
          label={
            <button
              type="button"
              onClick={() => void openGuarded(() => setIsCorrectionLetterModalOpen(true))}
              className="text-primary cursor-pointer text-left underline underline-offset-4"
            >
              {t("financial.correctionLetter")}
            </button>
          }
          value={hasCorrectionLetter ? t("financial.yes") : t("financial.no")}
        />
      </div>

      {/* Itemization of Amount Financed + Truth in Lending Disclosures */}
      <div className="gap-x-(--spacing-8) gap-y-(--spacing-4) grid grid-cols-1 sm:grid-cols-2">
        <div className="flex flex-col">
          <SubsectionHeader>{t("financial.itemizationSectionTitle")}</SubsectionHeader>

          {/* Selling price */}
          <div className="gap-(--spacing-1) mt-(--spacing-2) flex flex-col">
            <LineItemRow label={t("financial.lineItems.sellingPrice")} value={signedAmount(amounts.sellingPrice)} />
            <LineItemRow
              label={renderLabel("financial.lineItems.accessories", "accessories", hasAccessories)}
              value={signedAmount(amounts.accessories)}
            />
            <LineItemRow
              label={t("financial.lineItems.totalSellingPrice")}
              value={signedAmount(amounts.totalSellingPrice)}
              className="font-(--font-weight-semibold)"
            />
          </div>

          {/* Sales tax + down payment */}
          <div className="gap-(--spacing-1) mt-(--spacing-2) flex flex-col">
            <LineItemRow
              label={
                <span className="flex flex-col">
                  <span>{renderLabel("financial.lineItems.salesTax", "salesTax", hasSalesTaxLines)}</span>
                  {amounts.isSalesTaxManual && (
                    <span className="text-muted-foreground text-[10px]">{t("financial.salesTaxManuallyEntered")}</span>
                  )}
                </span>
              }
              value={signedAmount(amounts.salesTax)}
            />
            <LineItemRow
              label={renderLabel("financial.lineItems.cashDown", "deferredDownPayment", hasDeferredDownPayment)}
              value={signedAmount(amounts.cashDown)}
            />
            <LineItemRow label={t("financial.lineItems.grossTradeIn")} value={signedAmount(amounts.grossTradeIn)} />
            <LineItemRow
              label={t("financial.lineItems.tradeInPayoff")}
              value={signedAmount(amounts.tradeInPayoff, true)}
            />
          </div>

          {/* Total down payment */}
          <LineItemRow
            label={t("financial.lineItems.totalDownPayment")}
            value={signedAmount(amounts.totalDownPayment, true)}
            className={clsx("font-(--font-weight-bold) mt-(--spacing-2) pt-(--spacing-2)", underlineClassName)}
            valueClassName="text-primary"
          />

          {/* Fees and backend products */}
          <div className="gap-(--spacing-1) mt-(--spacing-2) flex flex-col">
            <LineItemRow label={t("financial.lineItems.vsc")} value={signedAmount(amounts.vsc)} />
            <LineItemRow label={t("financial.lineItems.gap")} value={signedAmount(amounts.gap)} />
            <LineItemRow
              label={t("financial.lineItems.otherBackendProducts")}
              value={signedAmount(amounts.otherBackendProducts)}
            />
            <LineItemRow
              label={renderLabel("financial.lineItems.totalCustomerFees", "customerFees", canOpenCustomerFees)}
              value={signedAmount(amounts.totalCustomerFees)}
            />
          </div>

          {/* Itemized amount financed */}
          <LineItemRow
            label={t("financial.lineItems.itemizedAmountFinanced")}
            value={formatOptional(amounts.amountFinanced, formatCurrency)}
            className={clsx("font-(--font-weight-bold) mt-(--spacing-2) pt-(--spacing-2)", underlineClassName)}
            valueClassName="text-primary"
          />

          {amounts.isSalesTaxManual && (
            <p className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) mt-(--spacing-3)">
              {t("financial.salesTaxFootnote")}
            </p>
          )}
        </div>

        {/* Truth in Lending Disclosures */}
        <div className="flex flex-col">
          <SubsectionHeader>{t("financial.truthInLendingSectionTitle")}</SubsectionHeader>

          {/* Vehicle */}
          <div className="gap-(--spacing-4) mt-(--spacing-3) grid grid-cols-2 sm:grid-cols-3">
            <FinancialField label={t("financial.truthInLending.newUsed")} value={vehicle?.condition || EMPTY_VALUE} />
            <FinancialField label={t("financial.truthInLending.year")} value={formatOptional(vehicle?.year, String)} />
            <FinancialField label={t("financial.truthInLending.make")} value={vehicle?.make || EMPTY_VALUE} />
            <FinancialField label={t("financial.truthInLending.model")} value={vehicle?.model || EMPTY_VALUE} />
            <FinancialField label={t("financial.truthInLending.vin")} value={vehicle?.vin || EMPTY_VALUE} />
            <FinancialField
              label={t("financial.truthInLending.mileage")}
              value={formatOptional(vehicle?.mileage, (mileage) => mileage.toLocaleString())}
            />
          </div>

          {/* Loan terms */}
          <div className="gap-(--spacing-4) border-border mt-(--spacing-4) pt-(--spacing-3) grid grid-cols-2 border-t sm:grid-cols-3">
            <FinancialField
              label={t("financial.truthInLending.apr")}
              value={formatOptional(tila?.apr, formatPercent)}
            />
            <FinancialField
              label={t("financial.truthInLending.financeCharge")}
              value={formatOptional(tila?.financeCharge, formatCurrency)}
            />
            <FinancialField
              label={t("financial.truthInLending.amountFinanced")}
              value={formatOptional(tila?.amountFinanced, formatCurrency)}
            />
            <FinancialField
              label={t("financial.truthInLending.totalOfPayments")}
              value={formatOptional(tila?.totalOfPayments, formatCurrency)}
            />
            <FinancialField
              label={t("financial.truthInLending.downPayment")}
              value={formatOptional(tila?.totalDownPayment, formatCurrency)}
            />
            <FinancialField
              label={t("financial.truthInLending.totalSalePrice")}
              value={formatOptional(tila?.totalSalePrice, formatCurrency)}
            />
            <FinancialField
              label={t("financial.truthInLending.intRate")}
              value={formatOptional(tila?.interestRate, formatPercent)}
            />
            <FinancialField
              label={t("financial.truthInLending.numberOfPayments")}
              value={formatOptional(tila?.term, String)}
            />
            <FinancialField
              label={t("financial.truthInLending.amountOfPayments")}
              value={formatOptional(tila?.monthlyPayment, formatCurrency)}
            />
            <FinancialField
              label={t("financial.truthInLending.paymentDueDate")}
              value={formatIsoDate(tila?.firstPaymentDate) || EMPTY_VALUE}
            />
          </div>
        </div>
      </div>

      {accessories && (
        <AccessoriesModal open={openModal === "accessories"} onOpenChange={closeModal} accessories={accessories} />
      )}
      {deferredDownPayment && (
        <DeferredDownPaymentModal
          open={openModal === "deferredDownPayment"}
          onOpenChange={closeModal}
          deferredDownPayment={deferredDownPayment}
          cashDown={amounts.cashDown}
          cashDownDate={contractDate}
          totalDown={amounts.totalDownPayment === undefined ? undefined : Math.abs(amounts.totalDownPayment)}
        />
      )}
      <SalesTaxModal
        open={openModal === "salesTax"}
        onOpenChange={closeModal}
        lines={salesTaxLines}
        total={amounts.salesTax}
        totalSellingPrice={amounts.totalSellingPrice}
      />
      <CustomerFeesModal
        open={openModal === "customerFees"}
        onOpenChange={closeModal}
        approvalId={approvalId}
        fees={customerFees}
        versionToken={financial?.versionToken}
        readOnly={readOnly}
        isFeeEstimated={financial?.feeEstimated}
        apr={tila?.apr}
        term={tila?.term}
        timeCountingMethod={(financial as FinancialWithFeeContext | undefined)?.timeCountingMethod}
        checkAccess={onCheckAccess}
      />

      <CorrectionLetterModal
        open={isCorrectionLetterModalOpen}
        onOpenChange={setIsCorrectionLetterModalOpen}
        approvalId={approvalId}
        readOnly={readOnly}
        checkAccess={onCheckAccess}
      />
    </Section>
  );
}

export default Financial;
